import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Send, KeyRound, ExternalLink, Calendar, Bell, CheckCircle2, Clock, RefreshCw } from 'lucide-react';
import { Card, SectionTitle, inputCls, btnPrimary, btnAccent, btnGhost } from './MlmShell';

// ── Milagros Villa Luy Onboarding Card ────────────────────────────────────────
const MvlOnboardingCard = ({ api, busy, baseUrl }) => {
  const [confirm, setConfirm] = useState(false);
  const [results, setResults] = useState(null);

  const sendOnboarding = async () => {
    const url = (baseUrl || 'https://www.neuralia369.com').replace(/\/$/, '');
    const res = await api('/admin/mvl/send-onboarding', { method: 'POST', body: JSON.stringify({ base_url: url }) }, { key: 'mvl-onb', silent: true });
    if (res?.results) setResults(res.results);
  };

  const MVL_LINES = [
    { label: 'Oriflame', color: '#9B8CC8', bg: '#F4F1FB', icon: '🌸', wp: '+51 944 029 206', url: 'https://shop.oriflame.com/PE-milagrosvillaluydirectorperu1205326028/cDr6yZs7W' },
    { label: 'FisioMedic', color: '#0D6EFD', bg: '#EFF9F6', icon: '🩺', wp: '+51 948 078 663', url: '' },
  ];

  return (
    <Card className="lg:col-span-3" testId="mlm-mvl-onboarding">
      <SectionTitle
        kicker="Nuevo cliente — Multi-línea"
        title="Milagros Villa Luy · Oriflame + FisioMedic"
        subtitle="Envía el email de bienvenida con credenciales y plan de pagos a las dos líneas de negocio de Milagros."
        brandColor="#9B8CC8"
      />
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        {MVL_LINES.map((line) => (
          <div key={line.label} style={{ backgroundColor: line.bg }} className="rounded-2xl p-4 ring-1 ring-slate-200">
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: line.color }}>{line.icon} {line.label}</p>
            <p className="mt-1 text-sm text-slate-700">WhatsApp: <b>{line.wp}</b></p>
            {line.url && <p className="mt-1 text-xs text-slate-500 break-all">{line.url.slice(0, 55)}…</p>}
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Plan de inversión MVL</p>
        <ul className="mt-2 space-y-1 text-sm text-slate-700">
          <li>✅ Pago activación: <b>$120 USD</b></li>
          <li>✅ Pago configuración: <b>$60 USD</b></li>
          <li>📅 Suscripción: <b>3 meses × $29/mes</b> = $87 USD</li>
          <li>🔄 Después: <b>Pay-per-consumption</b></li>
        </ul>
        <p className="mt-2 text-xs text-slate-500">Total inicial fijo: <b>$267 USD</b> · Email: serviciosysolucionesmvl@gmail.com</p>
      </div>
      {results && (
        <div className="mt-3 flex flex-wrap gap-2">
          {results.map((r) => (
            <span key={r.line} className={`rounded-full px-3 py-1 text-xs font-bold ${r.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`} data-testid={`mvl-onb-result-${r.line.toLowerCase()}`}>
              {r.success ? '✅' : '❌'} {r.line} → {r.email}
            </span>
          ))}
        </div>
      )}
      <label className="mt-4 flex items-start gap-3 rounded-2xl border border-[#9B8CC8]/30 bg-[#9B8CC8]/5 p-4 text-sm" data-testid="mvl-onb-confirm-label">
        <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-1" data-testid="mvl-onb-confirm" />
        <span>Confirmo enviar emails reales a <b>serviciosysolucionesmvl@gmail.com</b> con copia a mi correo admin. Usando URL base: <b>{baseUrl || 'https://www.neuralia369.com'}</b></span>
      </label>
      <button
        type="button"
        onClick={sendOnboarding}
        disabled={!confirm || busy === 'mvl-onb'}
        className="mt-3 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white transition hover:opacity-90 disabled:opacity-50"
        style={{ backgroundColor: '#9B8CC8' }}
        data-testid="mvl-onb-send-btn"
      >
        <Send className="h-4 w-4" />
        {busy === 'mvl-onb' ? 'Enviando...' : 'Enviar onboarding a Milagros (Oriflame + FisioMedic)'}
      </button>
    </Card>
  );
};

export const MlmAdminOnboarding = ({ api, busy }) => {
  const [data, setData] = useState(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [initBusy, setInitBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api('/admin/onboarding', {}, { silent: true, key: 'onb-load' });
    setData(res);
    setBaseUrl((b) => b || res.default_base_url);
  }, [api]);

  const loadSchedule = useCallback(async () => {
    const res = await api('/admin/subscription-schedule', {}, { silent: true, key: 'sub-load' });
    if (res?.schedule) setSchedule(res);
  }, [api]);

  useEffect(() => { load(); loadSchedule(); }, [load, loadSchedule]);

  if (!data) return <Card testId="mlm-admin-loading">Cargando…</Card>;
  const { contract, user, service, copy_email: cc, log, receipts = [], payment_plan: plan } = data;

  const createContract = async () => { await api('/admin/contract', { method: 'POST', body: JSON.stringify({ base_url: baseUrl }) }, { success: 'Contrato generado', key: 'onb-contract', silent: true }); load(); };
  const generateReceipt = async () => {
    if (!window.confirm('¿Generar el recibo del primer pago ($375 en efectivo), enviarlo a Laura y avisar al CEO por WhatsApp?')) return;
    await api('/admin/generate-receipt', { method: 'POST' }, { success: 'Recibo generado y CEO avisado', key: 'onb-receipt', silent: true });
    load();
  };
  const send = async (what) => {
    if (!confirm) return;
    const res = await api('/admin/send-onboarding', { method: 'POST', body: JSON.stringify({ base_url: baseUrl, what }) }, { key: `onb-send-${what}`, silent: true, success: 'Emails procesados' });
    setConfirm(false);
    load();
    return res;
  };

  const initSchedule = async () => {
    setInitBusy(true);
    const res = await api('/admin/subscription-schedule/init', { method: 'POST' }, { success: 'Calendario de suscripción inicializado', key: 'sub-init', silent: true });
    if (res?.schedule) setSchedule({ schedule: res.schedule, initialized: true });
    setInitBusy(false);
  };

  const markPaid = async (monthId, monthNum) => {
    if (!window.confirm(`¿Marcar la cuota ${monthNum}/3 ($69) como cobrada y generar el recibo?`)) return;
    const res = await api(`/admin/subscription-schedule/${monthId}`, { method: 'PATCH', body: JSON.stringify({ mark_paid: true }) }, { success: `Cuota ${monthNum}/3 cobrada. Recibo enviado a Laura.`, key: `sub-pay-${monthId}`, silent: true });
    if (res?.month) setSchedule((s) => ({ ...s, schedule: s.schedule.map((m) => m.id === monthId ? { ...m, ...res.month } : m) }));
  };

  const sendReminder = async (monthId, monthNum) => {
    const res = await api(`/admin/subscription-schedule/${monthId}/remind`, { method: 'POST' }, { success: `Recordatorio cuota ${monthNum}/3 enviado a Laura`, key: `sub-remind-${monthId}`, silent: true });
    if (res?.success) setSchedule((s) => ({ ...s, schedule: s.schedule.map((m) => m.id === monthId ? { ...m, reminder_sent: true } : m) }));
  };

  const updateChargeDate = async (monthId, newDate) => {
    const res = await api(`/admin/subscription-schedule/${monthId}`, { method: 'PATCH', body: JSON.stringify({ charge_date: newDate }) }, { success: 'Fecha actualizada', key: `sub-date-${monthId}`, silent: true });
    if (res?.month) setSchedule((s) => ({ ...s, schedule: s.schedule.map((m) => m.id === monthId ? { ...m, ...res.month } : m) }));
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <Card className="lg:col-span-2" testId="mlm-admin-onboarding">
        <SectionTitle kicker="Solo administración Neuralia369" title="Contrato + acceso de Laura" subtitle="Genera el contrato, revisa, y cuando lo decidas envía contrato y credenciales a Laura con copia a tu correo personal. Nada se envía sin tu confirmación." />
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl bg-[#f1f5fb] p-4" data-testid="mlm-admin-service">
            <p className="text-xs font-bold uppercase tracking-wider text-[#E2001A]">Servicio</p>
            <p className="mt-1 text-lg font-black text-[#062A5E]">{service.name}</p>
            <p className="text-2xl font-black text-[#0B3F8C]">${service.price_usd} USD</p>
            <ul className="mt-2 space-y-1 text-sm text-slate-600">{service.payment_terms.map((t) => <li key={t.label}>• {t.label}: <b>${t.amount_usd.toFixed(2)}</b></li>)}</ul>
            <p className="mt-2 text-xs text-slate-500">{service.delivery} · {service.scope}</p>
          </div>
          <div className="rounded-2xl bg-[#f1f5fb] p-4" data-testid="mlm-admin-credentials">
            <p className="text-xs font-bold uppercase tracking-wider text-[#E2001A]">Acceso de Laura</p>
            <p className="mt-1 text-sm text-slate-700"><KeyRound className="mr-1 inline h-3.5 w-3.5" />Usuario: <b>{user?.email}</b></p>
            <p className="text-sm text-slate-700">Contraseña temporal: <code className="rounded bg-white px-2 py-0.5">{data.temp_password}</code></p>
            <p className="mt-2 text-sm text-slate-700">Login: <b>{baseUrl}{data.login_path}</b></p>
            <p className="mt-2 text-xs text-slate-500">Copia a: {cc}</p>
          </div>
        </div>
        <div className="mt-4">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Dominio para los enlaces del email</label>
          <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} className={`${inputCls} mt-1`} data-testid="mlm-admin-base-url" />
          <p className="mt-1 text-xs text-slate-500">Producción: https://www.neuralia369.com (tras Deploy). Para pruebas puedes poner la URL del preview.</p>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {!contract && <button type="button" onClick={createContract} disabled={busy === 'onb-contract'} className={btnPrimary} data-testid="mlm-admin-create-contract"><FileText className="h-4 w-4" />Generar contrato</button>}
          {contract && (
            <>
              <span className="rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-700" data-testid="mlm-admin-contract-number">{contract.contract_number} · {contract.status}{contract.sent_at ? ` · enviado ${contract.sent_at.slice(0, 10)}` : ''}</span>
              <a href={`${process.env.REACT_APP_BACKEND_URL}${contract.pdf_path}`} target="_blank" rel="noreferrer" className={btnGhost} data-testid="mlm-admin-contract-pdf"><FileText className="h-4 w-4" />Ver PDF</a>
              <a href={contract.sign_path} target="_blank" rel="noreferrer" className={btnGhost} data-testid="mlm-admin-contract-sign-preview"><ExternalLink className="h-4 w-4" />Ver página de firma</a>
            </>
          )}
        </div>
        <label className="mt-5 flex items-start gap-3 rounded-2xl border border-[#E2001A]/30 bg-[#E2001A]/5 p-4 text-sm">
          <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-1" data-testid="mlm-admin-confirm" />
          <span>Confirmo que quiero enviar ahora los emails reales a <b>{user?.email}</b> con copia a <b>{cc}</b> usando los enlaces de <b>{baseUrl}</b>.</span>
        </label>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={() => send('contract')} disabled={!confirm || !contract || !!busy} className={btnPrimary} data-testid="mlm-admin-send-contract"><Send className="h-4 w-4" />Enviar solo contrato</button>
          <button type="button" onClick={() => send('access')} disabled={!confirm || !!busy} className={btnPrimary} data-testid="mlm-admin-send-access"><Send className="h-4 w-4" />Enviar solo acceso</button>
          <button type="button" onClick={() => send('both')} disabled={!confirm || !contract || !!busy} className={btnAccent} data-testid="mlm-admin-send-both"><Send className="h-4 w-4" />Enviar contrato + acceso</button>
        </div>
      </Card>

      <Card testId="mlm-admin-log">
        <SectionTitle kicker="Registro" title="Actividad de onboarding" />
        <ul className="mt-4 space-y-2 text-sm">
          {log.length === 0 && <li className="text-slate-500">Sin actividad todavía.</li>}
          {log.map((l) => <li key={l.id} className="rounded-xl bg-[#f1f5fb] px-3 py-2 text-slate-700"><span className="text-xs text-slate-400">{l.created_at?.slice(0, 16).replace('T', ' ')}</span><br />{l.event}</li>)}
        </ul>
        {contract && <p className="mt-4 text-xs text-slate-500"><ExternalLink className="mr-1 inline h-3 w-3" />El enlace de firma se genera con token seguro y se incluye en el email del contrato. Ruta pública: /contrato/{contract.id}</p>}
      </Card>

      {plan && (
        <Card className="lg:col-span-3" testId="mlm-admin-payments">
          <SectionTitle kicker="Cobros" title="Plan de pagos y recibos" subtitle="Recibo automático al firmar. Aquí puedes generarlo manualmente si el pago en efectivo ya se hizo, y avisar al CEO por WhatsApp." />
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="mlm-payment-first">
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">1º pago · Efectivo</p>
              <p className="mt-1 text-3xl font-black text-emerald-700">${plan.first_payment.amount_usd.toFixed(0)}</p>
              <p className="text-sm text-emerald-800">{plan.first_payment.label}</p>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="mlm-payment-second">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-700">2º pago · Pendiente</p>
              <p className="mt-1 text-3xl font-black text-amber-700">${plan.second_payment.amount_usd.toFixed(0)}</p>
              <p className="text-sm text-amber-800">{plan.second_payment.label}</p>
            </div>
            <div className="rounded-2xl border border-[#0B3F8C]/20 bg-[#f1f5fb] p-4" data-testid="mlm-payment-subscription">
              <p className="text-xs font-bold uppercase tracking-wider text-[#0B3F8C]">Suscripción</p>
              <p className="mt-1 text-3xl font-black text-[#0B3F8C]">${plan.subscription.total_usd.toFixed(0)}</p>
              <p className="text-sm text-slate-600">{plan.subscription.label}</p>
            </div>
          </div>
          <p className="mt-3 rounded-xl bg-[#fff7ed] px-3 py-2 text-sm text-amber-800" data-testid="mlm-payment-note">📌 {plan.note}</p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button type="button" onClick={generateReceipt} disabled={busy === 'onb-receipt'} className={btnAccent} data-testid="mlm-admin-generate-receipt"><FileText className="h-4 w-4" />{busy === 'onb-receipt' ? 'Generando…' : 'Generar recibo 1º pago + avisar CEO'}</button>
            {receipts.filter((r) => r.kind === 'primer_pago').map((r) => (
              <a key={r.id} href={`${process.env.REACT_APP_BACKEND_URL}${r.pdf_url}`} target="_blank" rel="noreferrer" className={btnGhost} data-testid={`mlm-receipt-${r.id}`}><FileText className="h-4 w-4" />{r.receipt_number} · ${r.amount_usd?.toFixed(0)}</a>
            ))}
          </div>
        </Card>
      )}

      {/* ── Suscripción mensual: 3 × $69 ── */}
      <Card className="lg:col-span-3" testId="mlm-admin-subscription">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <SectionTitle
            kicker="Suscripción de continuidad"
            title="3 cuotas × $69/mes"
            subtitle="Calendario automático desde la firma del contrato (30, 60 y 90 días). Aynara envía recordatorio 3 días antes de cada cargo. Tú confirmas el cobro manualmente y se genera el recibo."
          />
          <button
            type="button"
            onClick={initSchedule}
            disabled={initBusy}
            className={btnGhost}
            data-testid="mlm-sub-init-btn"
          >
            <RefreshCw className={`h-4 w-4 ${initBusy ? 'animate-spin' : ''}`} />
            {schedule?.initialized ? 'Recalcular calendario' : 'Inicializar calendario'}
          </button>
        </div>

        {!schedule || !schedule.schedule?.length ? (
          <div className="mt-5 rounded-2xl border-2 border-dashed border-[#0B3F8C]/20 bg-[#f1f5fb] p-6 text-center" data-testid="mlm-sub-empty">
            <Calendar className="mx-auto h-8 w-8 text-[#0B3F8C]/40" />
            <p className="mt-2 font-semibold text-[#062A5E]">Calendario sin inicializar</p>
            <p className="mt-1 text-sm text-slate-500">Haz clic en "Inicializar calendario" para calcular las fechas de las 3 cuotas a partir de la firma del contrato.</p>
          </div>
        ) : (
          <div className="mt-5 grid gap-4 sm:grid-cols-3" data-testid="mlm-sub-schedule">
            {schedule.schedule.map((month) => (
              <SubscriptionMonthCard
                key={month.id}
                month={month}
                onMarkPaid={() => markPaid(month.id, month.month_number)}
                onRemind={() => sendReminder(month.id, month.month_number)}
                onDateChange={(d) => updateChargeDate(month.id, d)}
                apiBaseUrl={process.env.REACT_APP_BACKEND_URL}
              />
            ))}
          </div>
        )}

        <p className="mt-4 text-xs text-slate-400">
          💡 Los recordatorios automáticos se envían cuando Aynara ejecuta el resumen diario matutino. También puedes enviarlos manualmente con el botón de cada cuota.
        </p>
      </Card>

      {/* ── Onboarding Milagros Villa Luy ─────────────────────────────── */}
      <MvlOnboardingCard api={api} busy={busy} baseUrl={baseUrl} />
    </div>
  );
};


const SubscriptionMonthCard = ({ month, onMarkPaid, onRemind, onDateChange, apiBaseUrl }) => {
  const [editDate, setEditDate] = useState(false);
  const [dateVal, setDateVal] = useState((month.charge_date || '').slice(0, 10));
  const isPaid = month.status === 'cobrado';
  const daysUntil = month.days_until_charge;

  const urgencyColor = isPaid
    ? 'border-emerald-200 bg-emerald-50'
    : daysUntil != null && daysUntil <= 3
    ? 'border-red-200 bg-red-50'
    : daysUntil != null && daysUntil <= 10
    ? 'border-amber-200 bg-amber-50'
    : 'border-[#0B3F8C]/20 bg-[#f1f5fb]';

  return (
    <div className={`rounded-2xl border p-4 ${urgencyColor}`} data-testid={`mlm-sub-month-${month.month_number}`}>
      <div className="flex items-center justify-between">
        <p className={`text-xs font-bold uppercase tracking-wider ${isPaid ? 'text-emerald-700' : 'text-[#0B3F8C]'}`}>
          Cuota {month.month_number}/3
        </p>
        {isPaid
          ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          : <Clock className="h-4 w-4 text-[#0B3F8C]/50" />}
      </div>
      <p className={`mt-1 text-3xl font-black ${isPaid ? 'text-emerald-700' : 'text-[#0B3F8C]'}`}>$69</p>

      {/* Fecha de cargo */}
      <div className="mt-2">
        {editDate ? (
          <div className="flex gap-1">
            <input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)}
              className="flex-1 rounded-lg border border-[#0B3F8C]/30 px-2 py-1 text-xs" />
            <button type="button" onClick={() => { onDateChange(dateVal); setEditDate(false); }}
              className="rounded-lg bg-[#0B3F8C] px-2 py-1 text-xs text-white">OK</button>
            <button type="button" onClick={() => setEditDate(false)}
              className="rounded-lg bg-slate-200 px-2 py-1 text-xs">×</button>
          </div>
        ) : (
          <button type="button" onClick={() => !isPaid && setEditDate(true)}
            className={`text-sm ${isPaid ? 'cursor-default text-emerald-700' : 'cursor-pointer text-slate-600 hover:text-[#0B3F8C] hover:underline'}`}
            data-testid={`mlm-sub-date-${month.month_number}`}>
            <Calendar className="mr-1 inline h-3.5 w-3.5" />
            {(month.charge_date || '').slice(0, 10)}
            {!isPaid && daysUntil != null && (
              <span className={`ml-2 text-xs font-semibold ${daysUntil <= 3 ? 'text-red-600' : daysUntil <= 10 ? 'text-amber-600' : 'text-slate-400'}`}>
                {daysUntil < 0 ? `${Math.abs(daysUntil)}d vencida` : daysUntil === 0 ? '¡Hoy!' : `en ${daysUntil}d`}
              </span>
            )}
          </button>
        )}
      </div>

      {isPaid ? (
        <div className="mt-3 space-y-1.5">
          <p className="text-xs font-semibold text-emerald-700">Cobrada el {(month.paid_at || '').slice(0, 10)}</p>
          {month.receipt_url && (
            <a href={`${apiBaseUrl}${month.receipt_url}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 text-xs text-[#0B3F8C] underline" data-testid={`mlm-sub-receipt-${month.month_number}`}>
              <FileText className="h-3.5 w-3.5" />Descargar recibo
            </a>
          )}
        </div>
      ) : (
        <div className="mt-3 flex flex-col gap-1.5">
          <button type="button" onClick={onMarkPaid}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-[#0B3F8C] px-3 py-2 text-xs font-bold text-white transition hover:bg-[#062A5E]"
            data-testid={`mlm-sub-pay-${month.month_number}`}>
            <CheckCircle2 className="h-3.5 w-3.5" />Marcar cobrada + recibo
          </button>
          <button type="button" onClick={onRemind}
            className={`flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-bold transition
              ${month.reminder_sent ? 'border-slate-200 bg-slate-100 text-slate-400' : 'border-[#E2001A]/40 bg-[#E2001A]/5 text-[#E2001A] hover:bg-[#E2001A]/10'}`}
            data-testid={`mlm-sub-remind-${month.month_number}`}>
            <Bell className="h-3.5 w-3.5" />
            {month.reminder_sent ? 'Recordatorio enviado' : 'Enviar recordatorio'}
          </button>
        </div>
      )}
    </div>
  );
};
